import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import {
  Button,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  useTheme,
  ButtonProps
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import InputSocialProfile from '../InputSocialProfile';
import { Box } from '@mui/system';
import { IObject } from 'src/models/objectManagement';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ProductTourTooltip from '../ProductTourTooltip';
import { reducerUpdateNextTourSteps } from 'src/features/productTour';
import NotAllowedTooltip from '../NotAllowedTooltip';

interface Props {
  title: string;
  buttonProps?: ButtonProps;
  buttonLabel?: string;
}

const ModalAddSocialProfile: React.FC<Props> = ({
  title,
  buttonProps = {},
  buttonLabel
}) => {
  const [open, setOpen] = useState(false);

  const dispatch = useAppDispatch();
  const {
    isOnTour,
    currentTour: { stepNumber, submenu }
  } = useAppSelector((state) => state.storeProductTour);

  const isCurrentlyOnTourStep4 =
    stepNumber === 4 && isOnTour && submenu === 'object';
  const isCurrentlyOnTourStep5 =
    stepNumber === 5 && isOnTour && submenu === 'object';

  const handleClickOpen = () => {
    setOpen(true);
    if (isCurrentlyOnTourStep4) {
      dispatch(reducerUpdateNextTourSteps());
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const theme = useTheme();

  const tempInputObjects = useAppSelector(
    (state) => state.objectManagement.tempInputObjects
  );

  const { basicObjectQuota, basicObjectQuotaUsed } = useAppSelector(
    (state) => state.users.project.onBoarding
  );

  const isOnBoarding = useAppSelector(
    (state) => state.users.project.onBoarding.isOnBoarding
  );

  const userRole = useAppSelector((state) => state.users.project.role);

  const isTourGroup = isOnTour && submenu === 'group';
  const isMember = userRole === 'member';

  return (
    <>
      <ProductTourTooltip placement="left-end" open={isCurrentlyOnTourStep4}>
        {isMember ? (
          <NotAllowedTooltip>
            <Button
              variant="outlined"
              endIcon={<AddIcon />}
              {...buttonProps}
              sx={
                isCurrentlyOnTourStep4
                  ? {
                      ...buttonProps.sx,
                      position: 'relative',
                      zIndex: 1000
                    }
                  : { ...buttonProps.sx }
              }
            >
              {buttonLabel ? buttonLabel : 'Social Profile'}
            </Button>
          </NotAllowedTooltip>
        ) : (
          <Button
            variant="outlined"
            onClick={!isTourGroup ? handleClickOpen : undefined}
            // sx={{
            //   color: theme.palette.primary.dark,
            //   border: `1px solid ${theme.palette.primary.dark}`
            // }}
            endIcon={<AddIcon />}
            {...buttonProps}
            sx={
              isCurrentlyOnTourStep4
                ? {
                    ...buttonProps.sx,
                    position: 'relative',
                    zIndex: 1000
                  }
                : { ...buttonProps.sx }
            }
          >
            {buttonLabel ? buttonLabel : 'Social Profile'}
          </Button>
        )}
      </ProductTourTooltip>
      <Dialog
        onClose={!isOnTour ? handleClose : undefined}
        open={open}
        fullWidth
        maxWidth={'md'}
      >
        <IconButton
          aria-label="close"
          onClick={!isOnTour ? handleClose : undefined}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <ProductTourTooltip
          placement="top"
          open={isCurrentlyOnTourStep5}
          onClick={handleClose}
          onClose={handleClose}
        >
          <DialogTitle
            sx={{
              m: !isOnTour ? theme.spacing(3, 3, 0) : undefined,
              p: theme.spacing(2, 3, 0, 3)
            }}
          >
            <Typography variant="h4">
              {title}{' '}
              <span
                style={{
                  marginLeft: theme.spacing(1),
                  fontWeight: 400,
                  color: theme.palette.primary.main
                }}
              >
                {basicObjectQuotaUsed}
              </span>
              {!isOnBoarding && tempInputObjects.length > 0 && (
                <span
                  style={{
                    fontWeight: 400,
                    color: theme.palette.success.main
                  }}
                >
                  {`(+${tempInputObjects.length})`}
                </span>
              )}
              /
              <span
                style={{
                  fontWeight: 400
                }}
              >
                {basicObjectQuota}
              </span>
            </Typography>
          </DialogTitle>
        </ProductTourTooltip>
        <DialogContent
          sx={{
            m: { xs: 0, sm: theme.spacing(1, 3) }
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="caption">
                Your quota is defined when your payment plan is purchased.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mt: 2 }}>
                <Typography variant="h5">
                  Add {buttonLabel ? buttonLabel : 'Social Profile'}
                </Typography>
                <InputSocialProfile
                  objectType="account"
                  onClose={handleClose}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ModalAddSocialProfile;
